import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faMoneyBillWave,
    faFilter,
    faTimes,
    faCalendarAlt,
    faSearch,
    faChevronLeft,
    faChevronRight,
    faCheck,
    faClock,
    faSpinner,
    faHistory
} from '@fortawesome/free-solid-svg-icons';
import api from '../../Api';
import { useAuth } from '../../contexts/AuthContext';

const PaymentHistory = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [batches, setBatches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Filters
    const [searchTerm, setSearchTerm] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [showFilters, setShowFilters] = useState(false);

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [perPage, setPerPage] = useState(10);

    useEffect(() => {
        fetchBatches();
    }, [currentPage, searchTerm, dateFrom, dateTo]);

    const fetchBatches = async () => {
        try {
            setLoading(true);
            // Section heads see batches from export stage, admins only see confirmed
            const sectionHeadRoles = ['resp_cati', 'chef_terrain', 'resp_controle'];
            const statusFilter = sectionHeadRoles.includes(user?.role) ? 'exported,confirmed' : 'confirmed';

            const response = await api.get('/payments', {
                params: {
                    page: currentPage,
                    per_page: perPage,
                    status: statusFilter,
                    search: searchTerm,
                    date_from: dateFrom,
                    date_to: dateTo,
                },
            });

            if (response.data.success) {
                setBatches(response.data.data.data);
                setCurrentPage(response.data.data.pagination.current_page);
                setTotalPages(response.data.data.pagination.last_page);
                setTotal(response.data.data.pagination.total);
            }
        } catch (err) {
            console.error('Error fetching payment history:', err);
            if (err.response?.data?.message) {
                setError(err.response.data.message);
            } else if (err.response?.status === 403) {
                setError('Accès non autorisé');
            } else if (err.response?.status === 401) {
                setError('Session expirée, veuillez vous reconnecter');
            } else {
                setError('Erreur lors du chargement de l\'historique des paiements');
            }
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };

    const handleClearFilters = () => {
        setSearchTerm('');
        setDateFrom('');
        setDateTo('');
        setCurrentPage(1);
    };

    const handleRowClick = (batchId) => {
        navigate(`/payments/${batchId}`);
    };

    const hasActiveFilters = searchTerm || dateFrom || dateTo;

    return (
        <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div className="min-w-0 flex-1">
                            <div className="flex items-center mb-2">
                                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 truncate">
                                    HISTORIQUE DES PAIEMENTS
                                </h1>
                            </div>
                            <p className="text-gray-600 text-sm sm:text-base">Consultez l'historique des lots de paiement confirmés</p>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-6 border border-gray-200">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
                        <div className="flex flex-col sm:flex-row gap-4 flex-1">
                            {/* Search */}
                            <div className="relative flex-1">
                                <FontAwesomeIcon
                                    icon={faSearch}
                                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                                />
                                <input
                                    type="text"
                                    placeholder="Rechercher par ID ou enquête..."
                                    value={searchTerm}
                                    onChange={(e) => {
                                        setSearchTerm(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                                />
                            </div>

                        </div>

                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                            <button
                                onClick={() => setShowFilters(!showFilters)}
                                className={`px-4 py-2 rounded-lg font-medium transition-colors duration-200 flex items-center justify-center ${
                                    showFilters || hasActiveFilters
                                        ? 'bg-red-600 text-white'
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                            >
                                <FontAwesomeIcon icon={faFilter} className="mr-2" />
                                Filtres
                            </button>
                            {hasActiveFilters && (
                                <button
                                    onClick={handleClearFilters}
                                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium transition-colors duration-200 flex items-center justify-center"
                                >
                                    <FontAwesomeIcon icon={faTimes} className="mr-2" />
                                    Effacer
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Extended Filters */}
                    {showFilters && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-gray-200">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    <FontAwesomeIcon icon={faCalendarAlt} className="mr-2 text-gray-400" />
                                    Date de début
                                </label>
                                <input
                                    type="date"
                                    value={dateFrom}
                                    onChange={(e) => {
                                        setDateFrom(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    <FontAwesomeIcon icon={faCalendarAlt} className="mr-2 text-gray-400" />
                                    Date de fin
                                </label>
                                <input
                                    type="date"
                                    value={dateTo}
                                    onChange={(e) => {
                                        setDateTo(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                                />
                            </div>
                        </div>
                    )}
                </div>

                {/* Error Message */}
                {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
                        {error}
                    </div>
                )}

                {/* Loading State */}
                {loading ? (
                    <div className="bg-white rounded-lg shadow-sm p-8 sm:p-12 text-center border border-gray-200">
                        <div className="animate-spin rounded-full h-8 w-8 sm:h-12 sm:w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
                        <p className="text-gray-600 text-sm sm:text-base">Chargement de l'historique...</p>
                    </div>
                ) : batches.length === 0 ? (
                    <div className="bg-white rounded-lg shadow-sm p-8 sm:p-12 text-center border border-gray-200">
                        <FontAwesomeIcon icon={faHistory} className="text-4xl sm:text-6xl text-gray-300 mb-4" />
                        <p className="text-gray-600 text-base sm:text-lg">Aucun lot confirmé trouvé</p>
                        <p className="text-gray-500 text-sm mt-2">Aucun lot de paiement n'a encore été confirmé</p>
                    </div>
                ) : (
                    <>
                        {/* Table */}
                        <div className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-200">
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                ID
                                            </th>
                                            <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Lot de paiement
                                            </th>
                                            <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Période
                                            </th>
                                            <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Lignes
                                            </th>
                                            <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Total Net
                                            </th>
                                            <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Confirmé le
                                            </th>
                                            <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Actions
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {batches.map((batch) => (
                                            <tr
                                                key={batch.id}
                                                onClick={() => handleRowClick(batch.id)}
                                                className="hover:bg-gray-50 cursor-pointer transition-colors duration-150"
                                            >
                                                <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                                                    <span className="text-sm font-medium text-gray-900">#{batch.id}</span>
                                                </td>
                                                <td className="px-3 sm:px-6 py-4">
                                                    {batch.creation_mode === 'auto' ? (
                                                        // Auto (weekly REM) batches span many surveys — show a short label.
                                                        <span className="text-sm font-medium text-gray-900">REM</span>
                                                    ) : batch.creation_mode === 'express' ? (
                                                        // Express batches are frais de déplacement payments.
                                                        <div className="flex flex-col">
                                                            <span className="text-sm font-medium text-gray-900">Frais de Déplacement :</span>
                                                            <span className="text-sm text-gray-900">
                                                                {(batch.surveys && batch.surveys.length > 0)
                                                                    ? batch.surveys.map(s => s.title).join(', ')
                                                                    : (batch.survey?.title || '-')}
                                                            </span>
                                                        </div>
                                                    ) : (batch.surveys && batch.surveys.length > 0) ? (
                                                        <div className="flex flex-col gap-1">
                                                            {batch.surveys.map((survey, idx) => (
                                                                <span key={idx} className="text-sm text-gray-900">
                                                                    {survey.title}
                                                                </span>
                                                            ))}
                                                            {batch.surveys.length > 1 && (
                                                                <span className="text-xs text-gray-500 font-medium">
                                                                    ({batch.surveys.length} enquêtes)
                                                                </span>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <span className="text-sm text-gray-900">
                                                            {batch.survey?.title || '-'}
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                                                    <span className="text-sm text-gray-900">
                                                        {new Date(batch.period_start).toLocaleDateString('fr-FR')} -{' '}
                                                        {new Date(batch.period_end).toLocaleDateString('fr-FR')}
                                                    </span>
                                                </td>
                                                <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                                                    <span className="text-sm text-gray-900">
                                                        {batch.lines_count}
                                                        {batch.lines_on_hold_count > 0 && (
                                                            <span className="ml-2 text-xs text-orange-600">
                                                                ({batch.lines_on_hold_count} en attente)
                                                            </span>
                                                        )}
                                                    </span>
                                                </td>
                                                <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                                                    {batch.creation_mode === 'express' && parseFloat(batch.credit_applied || 0) > 0 ? (
                                                        <div>
                                                            <span className="text-sm font-semibold text-purple-700">
                                                                {parseFloat(batch.net_a_decaisser).toFixed(2)} MAD
                                                            </span>
                                                            <div className="text-[11px] text-gray-400 line-through">{parseFloat(batch.totals_net).toFixed(2)} MAD</div>
                                                            <div className="text-[10px] text-gray-400">−{parseFloat(batch.credit_applied).toFixed(2)} caisse</div>
                                                        </div>
                                                    ) : (
                                                        <span className="text-sm font-semibold text-gray-900">
                                                            {parseFloat(batch.totals_net).toFixed(2)} MAD
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                                                    <div className="flex items-center">
                                                        <FontAwesomeIcon icon={faCheck} className="text-green-600 mr-2" />
                                                        <span className="text-sm text-gray-900">
                                                            {batch.confirmed_at ? new Date(batch.confirmed_at).toLocaleDateString('fr-FR') : '-'}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                                                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                                        <FontAwesomeIcon icon={faCheck} className="mr-1.5" />
                                                        Confirmé
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 p-4 mt-6">
                                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                                    <div className="text-sm text-gray-700 text-center sm:text-left">
                                        <span>
                                            Affichage de <span className="font-medium">{(currentPage - 1) * perPage + 1}</span> à{' '}
                                            <span className="font-medium">{Math.min(currentPage * perPage, total)}</span> sur{' '}
                                            <span className="font-medium">{total}</span> résultats
                                        </span>
                                    </div>
                                    {/* Mobile Pagination */}
                                    <div className="flex justify-center sm:hidden">
                                        <div className="flex space-x-2">
                                            <button
                                                onClick={() => handlePageChange(currentPage - 1)}
                                                disabled={currentPage === 1}
                                                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                <FontAwesomeIcon icon={faChevronLeft} className="w-4 h-4" />
                                            </button>
                                            <span className="px-3 py-2 text-sm font-medium text-gray-900">
                                                {currentPage} / {totalPages}
                                            </span>
                                            <button
                                                onClick={() => handlePageChange(currentPage + 1)}
                                                disabled={currentPage === totalPages}
                                                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                <FontAwesomeIcon icon={faChevronRight} className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>

                                    {/* Desktop Pagination */}
                                    <div className="hidden sm:block">
                                        <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                                            <button
                                                onClick={() => handlePageChange(currentPage - 1)}
                                                disabled={currentPage === 1}
                                                className="relative inline-flex items-center px-3 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                <FontAwesomeIcon icon={faChevronLeft} />
                                            </button>
                                            {[...Array(totalPages)].map((_, index) => {
                                                const page = index + 1;
                                                if (
                                                    page === 1 ||
                                                    page === totalPages ||
                                                    (page >= currentPage - 1 && page <= currentPage + 1)
                                                ) {
                                                    return (
                                                        <button
                                                            key={page}
                                                            onClick={() => handlePageChange(page)}
                                                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                                                currentPage === page
                                                                    ? 'z-10 bg-red-50 border-red-500 text-red-600'
                                                                    : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                                            }`}
                                                        >
                                                            {page}
                                                        </button>
                                                    );
                                                } else if (page === currentPage - 2 || page === currentPage + 2) {
                                                    return (
                                                        <span
                                                            key={page}
                                                            className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700"
                                                        >
                                                            ...
                                                        </span>
                                                    );
                                                }
                                                return null;
                                            })}
                                            <button
                                                onClick={() => handlePageChange(currentPage + 1)}
                                                disabled={currentPage === totalPages}
                                                className="relative inline-flex items-center px-3 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                <FontAwesomeIcon icon={faChevronRight} />
                                            </button>
                                        </nav>
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default PaymentHistory;
